import { useState, useContext, useRef, useEffect } from "react";
import Header from "../../Components/Header";
import Documents from "../../Components/Step3/Documents";
import Message from "../../Components/Step3/Message";
import ProgressBar from "../../Components/ProgressBar";
import { useNavigate } from "react-router-dom";
import "./step3manual.css";
import SkuRow from "./SkuRow";
import PortalContext from "../PortalContext";
import WeightQuestion from "../../Components/Step3/WeightQuestion";
import { useGetTranslation } from "../../Components/Translate";
import { CirclePlus } from "lucide-react";

export default function Step3Manual() {
  const { portal } = useContext(PortalContext);
  const getTranslation = useGetTranslation();

  console.log("Rendering Step-3-manual with portal:", portal);

  const navigate = useNavigate();
  const messageRef = useRef();

  const [weightQuestion, setWeightQuestion] = useState(
    portal?.step3manual?.weightQuestion ?? undefined
  );

  // Build initial "row labels" for rendering (keeps your current UX)
  const initialReturns = portal?.step3manual?.items?.length
    ? portal.step3manual.items.map((_, i) => `Return ${i + 1}`)
    : portal?.step3?.returns
    ? portal.step3.returns
    : ["Return 1"];

  const [returns, setReturns] = useState(initialReturns);

  // Build normalized items
  const buildInitialItems = () => {
    if (portal?.step3manual?.items?.length) return portal.step3manual.items;

    // otherwise, create blank rows matching `returns` count
    return initialReturns.map((_, index) => ({
      index,
      name: "",
      quantity: 1,
      reason: "",
      action: "",
      selected: true, // you can set true by default if you prefer
    }));
  };

  const [inputs, setInputs] = useState(() => ({
    items: buildInitialItems(),
    message: portal?.step3manual?.message || portal?.step3?.message || "",
    weightQuestion: portal?.step3manual?.weightQuestion ?? undefined,
  }));

  // patch a single manual item by index
  const setItem = (index, patch) => {
    setInputs((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], ...patch };
      return { ...prev, items };
    });
  };

  function addReturn() {
    const number = returns.length + 1;
    setReturns((r) => [...r, "Return " + number]);
    setInputs((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          index: prev.items.length,
          name: "",
          quantity: 1, // was 0
          reason: "",
          action: "",
          selected: true, // was false
        },
      ],
    }));
  }

  const deleteReturn = (value) => {
    setReturns((prevReturns) => {
      const idx = prevReturns.findIndex((x) => x === value);
      if (idx === -1) return prevReturns;

      // remove the row label
      const nextReturns = prevReturns.filter((x, i) => i !== idx);

      // remove the matching item and reindex the rest
      setInputs((prev) => {
        const nextItems = prev.items
          .filter((_, i) => i !== idx)
          .map((it, i) => ({
            ...it,
            index: i,
          }));
        return { ...prev, items: nextItems };
      });

      return nextReturns;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // For manual flow your system expects SKUs flag & a manual indicator
    portal.SKUs = 1;

    // Persist in normalized manual shape
    portal.step3manual = {
      ...inputs,
      message: messageRef.current?.value ?? inputs.message,
      weightQuestion,
      // documents: portal.documents || [],
    };

    navigate(`/step-4`);
  };

  useEffect(() => {
    if (!portal?.step2?.country) {
      navigate(`/`, { replace: true });
    }
  }, [portal, navigate]);

  return (
    <>
      <Header />

      <section class="page3-slider py-lg-3">
        <div class="container">
          <div class="row d-flex align-items-center">
            <div class="col-lg-12 col-12 pb-5">
              <ProgressBar step={3} />

              <h2>
                {getTranslation(
                  "STEP3_MANUAL_TITLE",
                  "What would you like to return?"
                )}
              </h2>

              <div class="align-items-center mt-3">
                <div class="row mt-4 headers-mobile">
                  <div class="col-sm-4">
                    <div class="header-title">
                      {getTranslation(
                        "STEP3_MANUAL_HEADER_PRODUCT",
                        "Product name"
                      )}
                    </div>
                  </div>

                  <div class="col-sm-1">
                    <div class="header-title">
                      {getTranslation(
                        "STEP3_MANUAL_HEADER_QUANTITY",
                        "Quantity"
                      )}
                    </div>
                  </div>
                  <div class="col-sm-3 dropdown-border">
                    <div class="header-title">
                      {" "}
                      {getTranslation("STEP3_MANUAL_HEADER_REASON", "Reason")}
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="header-title">
                      {getTranslation("STEP3_MANUAL_HEADER_ACTION", "Action")}
                    </div>
                  </div>
                  <div class="col-sm-1">
                    <div class="header-title"></div>
                  </div>
                </div>

                <div className="row d-sm-none mt-2 pr-2">
                  <div
                    className="col-12 d-flex justify-content-end align-items-center gap-2 text-success"
                    role="button"
                    onClick={addReturn}
                    aria-label="Add return row"
                  >
                    <span className="fw-bold">Add Item</span>
                    <CirclePlus className="w-5 h-5 ms-1 ml-2" />
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  {returns?.map((label, index) => (
                    <SkuRow
                      key={index}
                      item={label}
                      deleteReturn={deleteReturn}
                      addReturn={addReturn}
                      // NEW normalized data plumbed through:
                      itemState={inputs.items[index]}
                      setItem={setItem}
                    />
                  ))}

                  <div
                    className="cust-msg-para"
                    dangerouslySetInnerHTML={{
                      __html: portal?.data?.settings?.custMsgPara || "",
                    }}
                  />

                  <Message
                    messageRef={messageRef}
                    defaultMessage={inputs.message}
                  />

                  {portal?.data?.settings?.documents && <Documents />}

                  {portal?.data?.settings?.weightQuestions?.[
                    portal?.step2?.country
                  ] && (
                    <>
                      <WeightQuestion
                        weightQuestion={weightQuestion}
                        setWeightQuestion={setWeightQuestion}
                      />
                    </>
                  )}

                  <div class="row mt-4">
                    <div class="col-sm-8">
                      <button
                        type="submit"
                        style={{
                          backgroundColor: `${portal.data.settings.colours.primary}`,
                        }}
                      >
                        {getTranslation("CONTINUE_BUTTON", "Continue")}
                      </button>
                    </div>

                    <div class="col-sm-4 d-flex justify-content-end">
                      <img
                        src={require("../../Assets/Images/powered-by.png")}
                        class="img-fluid powered-logo"
                        alt=""
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
